import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import {
  addDays,
  addMonths,
  addQuarters,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  format,
  parseISO,
} from 'date-fns';
import { PERIOD_TYPE, PERFORMANCE_TAB } from '../types';
import type { PeriodType, PerformanceTab } from '../types';

const ISO = (d: Date) => format(d, 'yyyy-MM-dd');

function getWeekBounds(ref: Date) {
  return {
    start: ISO(startOfWeek(ref, { weekStartsOn: 1 })),
    end: ISO(endOfWeek(ref, { weekStartsOn: 1 })),
  };
}

function getMonthBounds(ref: Date) {
  return { start: ISO(startOfMonth(ref)), end: ISO(endOfMonth(ref)) };
}

function getQuarterBounds(ref: Date) {
  return { start: ISO(startOfQuarter(ref)), end: ISO(endOfQuarter(ref)) };
}

function getBoundsForType(ref: Date, type: PeriodType) {
  if (type === PERIOD_TYPE.MONTH) return getMonthBounds(ref);
  if (type === PERIOD_TYPE.QUARTER) return getQuarterBounds(ref);
  return getWeekBounds(ref);
}

interface LaborPerformanceStore {
  periodType: PeriodType;
  periodStart: string;
  periodEnd: string;
  activeTab: PerformanceTab;
  memberSearch: string;
  setPeriodType: (type: PeriodType) => void;
  navigatePeriod: (direction: 'prev' | 'next') => void;
  setActiveTab: (tab: PerformanceTab) => void;
  setMemberSearch: (q: string) => void;
}

const now = new Date();
const initialWeek = getWeekBounds(now);

export const useLaborPerformanceStore = create<LaborPerformanceStore>((set, get) => ({
  periodType: PERIOD_TYPE.WEEK,
  periodStart: initialWeek.start,
  periodEnd: initialWeek.end,
  activeTab: PERFORMANCE_TAB.TIMELINE,
  memberSearch: '',

  setPeriodType: (type) => {
    const bounds = getBoundsForType(now, type);
    set({ periodType: type, periodStart: bounds.start, periodEnd: bounds.end });
  },

  navigatePeriod: (direction) => {
    const { periodStart, periodType } = get();
    const ref = parseISO(periodStart);
    const delta = direction === 'next' ? 1 : -1;

    let next: Date;
    if (periodType === PERIOD_TYPE.WEEK) {
      next = addDays(ref, delta * 7);
    } else if (periodType === PERIOD_TYPE.MONTH) {
      next = addMonths(ref, delta);
    } else {
      next = addQuarters(ref, delta);
    }

    const bounds = getBoundsForType(next, periodType);
    set({ periodStart: bounds.start, periodEnd: bounds.end });
  },

  setActiveTab: (tab) => set({ activeTab: tab }),

  setMemberSearch: (q) => set({ memberSearch: q }),
}));

export function usePeriod() {
  return useLaborPerformanceStore(
    useShallow((s) => ({
      periodType: s.periodType,
      periodStart: s.periodStart,
      periodEnd: s.periodEnd,
      navigatePeriod: s.navigatePeriod,
      setPeriodType: s.setPeriodType,
    })),
  );
}

export function useActiveTab() {
  return useLaborPerformanceStore(
    useShallow((s) => ({ activeTab: s.activeTab, setActiveTab: s.setActiveTab })),
  );
}

export function useMemberSearch() {
  return useLaborPerformanceStore(
    useShallow((s) => ({ memberSearch: s.memberSearch, setMemberSearch: s.setMemberSearch })),
  );
}
